<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import axios from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Archive, Download, FilePlus2, Save, Search, Upload, X } from 'lucide-vue-next'
import { useToast } from '@/components/ui/toast'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ project: any; documents: any[]; processes: any[]; capabilities: any; options: any }>()
const emit = defineEmits<{ (event: 'reload'): void }>()
const { t } = useI18n()
const { toast } = useToast()
const selected = ref<any>(null)
const loading = ref(false)
const createOpen = ref(false)
const search = ref('')
const uploadFile = ref<File | null>(null)
const revisionFile = ref<File | null>(null)
const form = ref({ title: '', description: '', category: '', visibility: 'module' })
const filteredDocuments = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return props.documents
  return props.documents.filter((document) => `${document.title} ${document.description || ''} ${document.category || ''}`.toLowerCase().includes(term))
})

watch(() => props.documents, (documents) => {
  if (selected.value) {
    const current = documents.find((item) => item.id === selected.value.id)
    if (!current) selected.value = null
  }
})

async function openDocument(id: number) {
  const response = await axios.get(`/process_management/documents/${id}`)
  selected.value = response.data.document
}

function fileFrom(event: Event, target: 'upload' | 'revision') {
  const file = (event.target as HTMLInputElement).files?.[0] || null
  if (target === 'upload') uploadFile.value = file
  else revisionFile.value = file
}

async function createDocument() {
  if (!uploadFile.value) return
  loading.value = true
  const payload = new FormData()
  Object.entries(form.value).forEach(([key, value]) => payload.append(key, value))
  payload.append('file', uploadFile.value)
  try {
    const response = await axios.post(`/process_management/projects/${props.project.id}/documents`, payload)
    createOpen.value = false
    form.value = { title: '', description: '', category: '', visibility: 'module' }
    uploadFile.value = null
    emit('reload')
    selected.value = response.data.document
  } catch (error: any) {
    toast({ title: t('processManagement.uploadFailed'), description: error.response?.data?.error, variant: 'destructive' })
  } finally { loading.value = false }
}

async function addRevision() {
  if (!revisionFile.value || !selected.value) return
  const payload = new FormData()
  payload.append('file', revisionFile.value)
  payload.append('change_note', selected.value.new_change_note || '')
  await axios.post(`/process_management/documents/${selected.value.id}/revision`, payload)
  revisionFile.value = null
  await openDocument(selected.value.id)
  emit('reload')
}

async function saveSharing() {
  const response = await axios.patch(`/process_management/documents/${selected.value.id}/sharing`, {
    visibility: selected.value.visibility,
    user_ids: selected.value.shared_user_ids || [],
    function_ids: selected.value.shared_function_ids || [],
  })
  selected.value = response.data.document
  emit('reload')
}

async function saveLinks() {
  const response = await axios.patch(`/process_management/documents/${selected.value.id}/links`, {
    process_ids: selected.value.process_ids || [],
    related_document_ids: selected.value.related_document_ids || [],
  })
  selected.value = response.data.document
  emit('reload')
}

async function saveMetadata() {
  const response = await axios.patch(`/process_management/documents/${selected.value.id}`, {
    document: {
      title: selected.value.title,
      description: selected.value.description,
      category: selected.value.category,
      lock_version: selected.value.lock_version,
    },
  })
  selected.value = response.data.document
  emit('reload')
  toast({ title: t('processManagement.saved') })
}

async function archiveDocument() {
  if (!window.confirm(t('processManagement.archiveDocumentConfirm'))) return
  await axios.post(`/process_management/documents/${selected.value.id}/archive`, { lock_version: selected.value.lock_version })
  selected.value = null
  emit('reload')
}

function userName(user: any) {
  return [user.firstname, user.lastname].filter(Boolean).join(' ') || `#${user.id}`
}
</script>

<template>
  <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
    <section class="rounded-xl border bg-white shadow-sm">
      <div class="flex items-center justify-between border-b p-4">
        <div><h2 class="font-semibold">{{ t('processManagement.media') }}</h2><p class="text-sm text-slate-500">{{ t('processManagement.mediaDescription') }}</p></div>
        <Button v-if="capabilities.can_edit" @click="createOpen = !createOpen"><FilePlus2 class="mr-2 h-4 w-4" />{{ t('processManagement.upload') }}</Button>
      </div>
      <div class="relative border-b p-3"><Search class="absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input v-model="search" class="pl-9" :placeholder="t('processManagement.searchDocuments')" /></div>
      <form v-if="createOpen" class="grid gap-3 border-b bg-slate-50 p-4 sm:grid-cols-2" @submit.prevent="createDocument">
        <div><Label>{{ t('processManagement.documentTitle') }}</Label><Input v-model="form.title" required /></div>
        <div><Label>{{ t('processManagement.documentCategory') }}</Label><Input v-model="form.category" /></div>
        <div class="sm:col-span-2"><Label>{{ t('processManagement.description') }}</Label><textarea v-model="form.description" rows="2" class="w-full rounded-md border p-2" /></div>
        <div><Label>{{ t('processManagement.visibility') }}</Label><select v-model="form.visibility" class="h-10 w-full rounded-md border bg-white px-3"><option value="module">{{ t('processManagement.moduleWide') }}</option><option value="restricted">{{ t('processManagement.restricted') }}</option></select></div>
        <div><Label>{{ t('processManagement.file') }}</Label><input type="file" required class="block w-full text-sm" @change="fileFrom($event, 'upload')" /></div>
        <div class="sm:col-span-2"><Button type="submit" :disabled="loading"><Upload class="mr-2 h-4 w-4" />{{ t('processManagement.upload') }}</Button></div>
      </form>
      <div class="divide-y">
        <button v-for="document in filteredDocuments" :key="document.id" class="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-slate-50" @click="openDocument(document.id)">
          <div><div class="font-medium text-slate-900">{{ document.title }}</div><div class="text-xs text-slate-500">{{ document.category || '—' }} · {{ document.current_revision?.filename || t('processManagement.noFile') }}</div></div>
          <span class="rounded bg-slate-100 px-2 py-1 text-xs">v{{ document.current_revision?.revision_number || 0 }}</span>
        </button>
        <p v-if="!filteredDocuments.length" class="p-8 text-center text-sm text-slate-500">{{ t('processManagement.noDocuments') }}</p>
      </div>
    </section>

    <aside v-if="selected" class="rounded-xl border bg-white p-5 shadow-sm">
      <div class="flex items-start justify-between"><div><h2 class="font-semibold">{{ selected.title }}</h2><p class="text-sm text-slate-500">{{ selected.description }}</p></div><Button size="icon" variant="ghost" @click="selected = null"><X class="h-4 w-4" /></Button></div>
      <div class="mt-5 space-y-5">
        <section v-if="capabilities.can_edit" class="space-y-2 rounded border bg-slate-50 p-3"><div><Label>{{ t('processManagement.documentTitle') }}</Label><Input v-model="selected.title" /></div><div><Label>{{ t('processManagement.documentCategory') }}</Label><Input v-model="selected.category" /></div><div><Label>{{ t('processManagement.description') }}</Label><textarea v-model="selected.description" rows="3" class="w-full rounded-md border p-2" /></div><div class="flex gap-2"><Button size="sm" @click="saveMetadata"><Save class="mr-2 h-4 w-4" />{{ t('processManagement.save') }}</Button><Button size="sm" variant="outline" class="text-red-600" @click="archiveDocument"><Archive class="mr-2 h-4 w-4" />{{ t('processManagement.archive') }}</Button></div></section>
        <section><h3 class="mb-2 text-sm font-semibold">{{ t('processManagement.revisions') }}</h3><div class="space-y-2"><div v-for="revision in selected.revisions" :key="revision.id" class="flex items-center justify-between rounded border p-2 text-sm"><div><div>v{{ revision.revision_number }} · {{ revision.filename }}</div><div class="text-xs text-slate-500">{{ revision.change_note || '—' }}</div></div><a :href="revision.download_url" target="_blank"><Button size="icon" variant="ghost"><Download class="h-4 w-4" /></Button></a></div></div></section>
        <section v-if="capabilities.can_edit" class="rounded border bg-slate-50 p-3"><Label>{{ t('processManagement.newRevision') }}</Label><Input v-model="selected.new_change_note" class="my-2" :placeholder="t('processManagement.changeNote')" /><input type="file" class="block w-full text-sm" @change="fileFrom($event, 'revision')" /><Button size="sm" class="mt-3" @click="addRevision">{{ t('processManagement.upload') }}</Button></section>
        <section v-if="capabilities.can_edit"><h3 class="mb-2 text-sm font-semibold">{{ t('processManagement.sharing') }}</h3><select v-model="selected.visibility" class="h-10 w-full rounded-md border bg-white px-3"><option value="module">{{ t('processManagement.moduleWide') }}</option><option value="restricted">{{ t('processManagement.restricted') }}</option></select><div v-if="selected.visibility === 'restricted'" class="mt-2 max-h-40 space-y-2 overflow-auto rounded border p-2"><div><div class="mb-1 text-xs font-semibold text-slate-500">{{ t('processManagement.sharingUsers') }}</div><label v-for="user in options.users" :key="`user-${user.id}`" class="flex gap-2 text-sm"><input v-model="selected.shared_user_ids" type="checkbox" :value="user.id" />{{ userName(user) }}</label></div><div><div class="mb-1 text-xs font-semibold text-slate-500">{{ t('processManagement.sharingFunctions') }}</div><label v-for="item in options.functions" :key="`function-${item.id}`" class="flex gap-2 text-sm"><input v-model="selected.shared_function_ids" type="checkbox" :value="item.id" />{{ item.displayname || item.functionname }}</label></div></div><Button size="sm" class="mt-2" @click="saveSharing">{{ t('processManagement.save') }}</Button></section>
        <section v-if="capabilities.can_edit"><h3 class="mb-2 text-sm font-semibold">{{ t('processManagement.links') }}</h3><div class="max-h-44 space-y-1 overflow-auto rounded border p-2"><label v-for="process in processes" :key="process.id" class="flex gap-2 text-sm"><input v-model="selected.process_ids" type="checkbox" :value="process.id" />{{ process.code }} · {{ process.title }}</label><label v-for="document in documents.filter((item) => item.id !== selected.id)" :key="document.id" class="flex gap-2 text-sm"><input v-model="selected.related_document_ids" type="checkbox" :value="document.id" />{{ document.title }}</label></div><Button size="sm" class="mt-2" @click="saveLinks">{{ t('processManagement.save') }}</Button></section>
      </div>
    </aside>
  </div>
</template>
